import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api, resolveMediaUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const PLACEHOLDER_GRID = [
  { url: "https://images.pexels.com/photos/18599094/pexels-photo-18599094.jpeg", aspect: "portrait" },
  { url: "https://images.pexels.com/photos/9499851/pexels-photo-9499851.jpeg", aspect: "landscape" },
  { url: "https://images.pexels.com/photos/10194706/pexels-photo-10194706.jpeg", aspect: "portrait" },
  { url: "https://images.unsplash.com/photo-1766153550988-121007d31002", aspect: "landscape" },
];

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    api.get("/photos")
      .then(({ data }) => setPhotos(data))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => setActive(-1), []);
  const prev = useCallback(() => setActive((i) => (i <= 0 ? photos.length - 1 : i - 1)), [photos.length]);
  const next = useCallback(() => setActive((i) => (i >= photos.length - 1 ? 0 : i + 1)), [photos.length]);

  useEffect(() => {
    function onKey(e) {
      if (active < 0) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = active >= 0 ? "hidden" : "";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, prev, next]);

  const showEmpty = !loading && photos.length === 0;

  return (
    <div data-testid="gallery-page" className="bg-[#1A1514] min-h-screen">
      <section className="pt-40 md:pt-48 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Chapter II</p>
        </Reveal>
        <Reveal delay={150}>
          <h1
            data-testid="gallery-title"
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl leading-none tracking-tighter text-ink-ivory"
          >
            Gallery
          </h1>
        </Reveal>
        <Reveal delay={350}>
          <p className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-ink-ivory/65">
            Photographs sequenced as if mounted on a gallery wall — printed,
            edited, breathing in space. Click any image to enter the full plate.
          </p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 max-w-7xl mx-auto">
        {loading ? (
          <GallerySkeleton />
        ) : showEmpty ? (
          <GalleryEmpty />
        ) : (
          <GalleryGrid photos={photos} onOpen={setActive} />
        )}
      </section>

      {active >= 0 && photos[active] && (
        <Lightbox
          photo={photos[active]}
          index={active}
          total={photos.length}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
}

function GalleryGrid({ photos, onOpen }) {
  // Asymmetric editorial grid
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-5">
      {photos.map((p, i) => {
        // Pattern: every 5th item spans 2 cols & 2 rows; others vary by orientation
        const isFeatured = (i % 7 === 0);
        const isPortrait = p.orientation === "portrait";
        const span = isFeatured
          ? "col-span-2 md:col-span-3 row-span-2"
          : isPortrait
            ? "col-span-1 md:col-span-2"
            : "col-span-2 md:col-span-3";
        const aspect = isFeatured ? "aspect-[4/5]" : isPortrait ? "aspect-portrait" : "aspect-landscape";
        return (
          <Reveal key={p.id} delay={(i % 6) * 80} className={span}>
            <button
              type="button"
              onClick={() => onOpen(i)}
              data-testid={`gallery-tile-${p.id}`}
              className={`relative block w-full img-hover overflow-hidden border border-ink-ivory/10 bg-[#1F1817] ${aspect}`}
              aria-label={p.caption || `Photograph ${i + 1}`}
            >
              <img src={resolveMediaUrl(p.image_url)} alt={p.caption || ""} className="w-full h-full object-cover" />
            </button>
          </Reveal>
        );
      })}
    </div>
  );
}

function Lightbox({ photo, index, total, onClose, onPrev, onNext }) {
  if (typeof document === "undefined") return null;

  const overlay = (
    <div
      data-testid="gallery-lightbox"
      className="animate-soft-fade"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        background: "rgba(15, 12, 11, 0.96)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-5 md:py-7">
        <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
          Plate {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          data-testid="lightbox-close"
          className="text-[10px] tracking-[0.32em] uppercase text-ink-ivory editorial-link"
        >
          Close ✕
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 md:px-16 pb-6">
        <img
          src={resolveMediaUrl(photo.image_url)}
          alt={photo.caption || ""}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxHeight: "78vh",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
            cursor: "default",
          }}
          draggable={false}
        />
      </div>

      <div className="px-6 md:px-10 pb-8 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          data-testid="lightbox-prev"
          className="text-[10px] tracking-[0.32em] uppercase text-ink-ivory editorial-link"
        >
          ← Previous
        </button>
        <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe text-center pointer-events-none">
          {photo.caption || "Untitled study"}
        </p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          data-testid="lightbox-next"
          className="text-[10px] tracking-[0.32em] uppercase text-ink-ivory editorial-link"
        >
          Next →
        </button>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

function GallerySkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-5">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`bg-[#1F1817] border border-ink-ivory/10 animate-pulse ${
            i % 7 === 0 ? "col-span-2 md:col-span-3 row-span-2 aspect-[4/5]" :
            i % 2 === 0 ? "col-span-1 md:col-span-2 aspect-portrait" : "col-span-2 md:col-span-3 aspect-landscape"
          }`}
        />
      ))}
    </div>
  );
}

function GalleryEmpty() {
  return (
    <div data-testid="gallery-empty" className="max-w-5xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-5">
        {PLACEHOLDER_GRID.map((p, i) => (
          <Reveal
            key={i}
            delay={i * 120}
            className={
              i === 0 ? "col-span-2 md:col-span-3 row-span-2" :
              p.aspect === "portrait" ? "col-span-1 md:col-span-2" : "col-span-2 md:col-span-3"
            }
          >
            <div className={`relative overflow-hidden border border-ink-ivory/10 ${
              i === 0 ? "aspect-[4/5]" : p.aspect === "portrait" ? "aspect-portrait" : "aspect-landscape"
            }`}>
              <img src={p.url} alt="" className="w-full h-full object-cover opacity-40 grayscale" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne/80">Curating</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-16 text-center text-base text-ink-ivory/65 max-w-2xl mx-auto leading-relaxed">
        The next set of photographs is being printed and sequenced.
        New plates appear here as quietly as they do on a gallery wall.
      </p>
      <p className="mt-6 text-center text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
        Volume II — In Preparation
      </p>
    </div>
  );
}
